/**
 * brand.js — hostname-to-brand mapper.
 *
 * Mar 3 2026 · Michael is buying two more domains at GoDaddy:
 *   • soulumo.app         → Soulumo product front door
 *   • onemillionlights.org → the campaign, chrome-less
 *
 * We keep ONE deployed app and switch the front door based on the
 * hostname the browser used. No infra changes, no separate build.
 *
 * The three brand modes:
 *   • reclaim  — the full ecosystem (today's default)
 *   • soulumo  — Soulumo-only landing + product experience
 *   • oml      — One Million Lights, campaign-only
 *
 * A `?brand=soulumo` query param overrides the hostname during
 * local development / preview testing so we can pretend we're on
 * a different domain without editing /etc/hosts.
 */

// Which hostname substrings trigger which brand. Kept as arrays so
// staging / preview subdomains can be added without touching logic.
const HOST_MAP = [
  { brand: "oml",     patterns: ["onemillionlights.", "1millionlights.", "onelight."] },
  { brand: "soulumo", patterns: ["soulumo."] },
];

export function resolveBrand() {
  if (typeof window === "undefined") return "reclaim";

  const host = (window.location.host || "").toLowerCase();

  // Hostname is authoritative on real branded domains — never let a
  // stale sessionStorage override override the real site.
  //
  // Feb 27 2026 · Michael got trapped on Soulumo when trying to log
  // in to Reclaim because an earlier `?brand=soulumo` query-param
  // override had persisted across navigations. On real branded
  // hosts the hostname is truth; on preview (no branded host match)
  // we still honour ?brand= for one navigation only.
  for (const { brand, patterns } of HOST_MAP) {
    if (patterns.some((p) => host.startsWith(p) || host.includes("." + p) || host.includes(p))) {
      // Clear any stray sessionStorage override — hostname wins.
      try { sessionStorage.removeItem("__brand_override"); } catch (_) {}
      return brand;
    }
  }

  // Preview hostname (no branded match). Allow one-shot brand override
  // via `?brand=…` — NOT persisted across navigations so the user
  // can never get stuck on the wrong brand by accident.
  try {
    const q = new URLSearchParams(window.location.search).get("brand");
    if (q === "soulumo" || q === "oml" || q === "reclaim") {
      return q;
    }
    // Explicit clear if someone bookmarked ?brand=clear.
    if (q === "clear") {
      try { sessionStorage.removeItem("__brand_override"); } catch (_) {}
    }
  } catch (_) { /* private-mode / SSR — fall through */ }

  return "reclaim";
}

// Convenience: is this brand a "narrowed" experience where we hide
// most of the Reclaim chrome?
export function isBrandedNarrow(brand) {
  return brand === "soulumo" || brand === "oml";
}

// The route the brand's front door (`/`) should land on.
export function brandHomePath(brand) {
  if (brand === "oml")     return "/one-million-lights";
  if (brand === "soulumo") return "/soulumo";
  return null; // reclaim uses the default landing
}
